// PublicWebShop — buyer-facing storefront at /webshop/:slug
// - No auth. Reads /api/webshop/shop/public/:slug and lists products.
// - Fulfillment badges, WA-order button per product, order-log to seller.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle, ShoppingBag, MapPin, Truck, Store as StoreIcon, Play, FileText, Heart } from 'lucide-react';
import api from '../utils/api';

const API = process.env.REACTOP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || '';
const abs = (u) => (!u ? '' : u.startsWith('http') ? u : `${API}${u}`);

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function PublicWebShop() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState(null);
  const [buyer, setBuyer] = useState({ name: '', phone: '', city: '', quantity: 1, note: '' });
  const [busy, setBusy] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const wishKey = `webshop_wishlist_${slug}`;
  const [wishlist, setWishlist] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(wishKey) || '[]')); } catch { return new Set(); }
  });

  const saveWishlist = (setNext) => {
    setWishlist(setNext);
    try { localStorage.setItem(wishKey, JSON.stringify([...setNext])); } catch {}
  };

  const toggleWishlist = async (e, p) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const alreadyLiked = wishlist.has(p.id);
    const next = new Set(wishlist);
    if (alreadyLiked) {
      next.delete(p.id);
      saveWishlist(next);
      toast('❤️ removed from wishlist');
      return;
    }
    // Ask phone once, remember locally for future
    let phone = localStorage.getItem('webshop_buyer_phone') || '';
    if (!phone) {
      phone = window.prompt('📱 Phone number (WhatsApp) — dukandaar aapko notify karega jab stock aayega:', '') || '';
      phone = phone.replace(/\s+/g, '').trim();
      if (phone) localStorage.setItem('webshop_buyer_phone', phone);
    }
    let name = localStorage.getItem('webshop_buyer_name') || '';
    if (!name && phone) {
      name = window.prompt('👤 Aapka naam (optional):', '') || '';
      if (name) localStorage.setItem('webshop_buyer_name', name);
    }
    next.add(p.id);
    saveWishlist(next);
    try {
      await api.post('/webshop/wishlist/add', {
        slug, product_id: p.id,
        buyer_phone: phone || null,
        buyer_name: name || null,
        buyer_city: buyer.city || null,
      });
      toast.success('❤️ Wishlist me add ho gaya · dukandaar notify karega');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Wishlist save failed');
    }
  };

  useEffect(() => {
    api.get(`/webshop/shop/public/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Shop not found'));
    // Track shop view once
    api.post('/webshop/analytics/track', { slug, kind: 'shop_view' }).catch(() => {});
  }, [slug]);

  // Capture PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Track product view whenever the modal opens on a new product
  useEffect(() => {
    if (!pick) return;
    api.post('/webshop/analytics/track', { slug, kind: 'product_view', product_id: pick.id }).catch(() => {});
  }, [pick, slug]);

  const installPWA = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice?.outcome === 'accepted') toast.success('✅ Home-screen pe install ho gaya');
    setInstallEvent(null);
  };

  const shop = data?.shop || {};
  const products = data?.products || [];
  const shopFulfillment = shop.fulfillment || 'both';
  const cities = useMemo(
    () => (shop.delivery_cities || '').split(',').map((s) => s.trim()).filter(Boolean),
    [shop.delivery_cities]
  );

  const openWA = async () => {
    if (!pick) return;
    if (!buyer.phone && !buyer.name) {
      const ok = window.confirm('Naam/phone ke bina bhejein? WhatsApp me shop-owner ke saath aap manually detail dena hoga.');
      if (!ok) return;
    }
    setBusy(true);
    try {
      // Log the order-click to seller notification queue
      await api.post('/webshop/orders/log', {
        slug,
        product_id: pick.id,
        buyer_name: buyer.name || null,
        buyer_phone: buyer.phone || null,
        buyer_city: buyer.city || null,
        quantity: Number(buyer.quantity) || 1,
        note: buyer.note || null,
      }).catch(() => {}); // don't block WA even if logging fails

      const priceLine = `₹${pick.price}${pick.moq ? ` (MOQ ${pick.moq})` : ''}`;
      const fulfil = pick.fulfillment || shopFulfillment;
      const fulfilLine = fulfil === 'online_delivery'
        ? '🚚 Home delivery mangwana chahta hoon'
        : fulfil === 'offline_pickup'
        ? '🏪 Pickup karne aaunga'
        : '🚚/🏪 Delivery ya pickup — batao';
      const buyerLine = [
        buyer.name && `👤 ${buyer.name}`,
        buyer.phone && `📞 ${buyer.phone}`,
        buyer.city && `📍 ${buyer.city}`,
        buyer.quantity && `Qty: ${buyer.quantity}`,
      ].filter(Boolean).join('\n');
      const noteLine = buyer.note ? `\n📝 ${buyer.note}` : '';
      const shareOg = `${API}/api/share/webshop/${slug}/${pick.id}`;
      const msg = `Namaste! Aapki shop se yeh product order karna hai:\n\n*${pick.name}*\n💰 ${priceLine}\n${fulfilLine}\n\n${buyerLine}${noteLine}\n\n🔗 ${shareOg}`;

      const waNum = (shop.whatsapp || '').replace(/[^0-9]/g, '');
      const url = waNum
        ? `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, '_blank');
      toast.success('✅ WhatsApp khul raha hai · shop owner ko bhi notification mil gayi');
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
      <div className="text-center">
        <div className="text-5xl mb-3">🏪</div>
        <div className="text-xl font-black mb-1">Shop nahi mila</div>
        <div className="opacity-70 text-sm">{err}</div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">⏳ Loading shop…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/40 to-slate-950 text-white pb-20" data-testid="public-webshop">
      {/* Shop header */}
      <div className="relative">
        {shop.banner_url ? (
          <img src={abs(shop.banner_url)} alt="" className="w-full h-40 sm:h-56 object-cover" />
        ) : (
          <div className="w-full h-40 sm:h-56 bg-gradient-to-r from-emerald-700 via-teal-700 to-amber-600" />
        )}
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-x-0 bottom-0 p-4 flex items-end gap-3">
          {shop.logo_url && (
            <img src={abs(shop.logo_url)} alt="" className="w-16 h-16 rounded-xl border-2 border-white object-cover shrink-0" />
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-black truncate" data-testid="public-shop-name">{shop.name}</h1>
            {shop.tagline && <div className="text-xs opacity-80 truncate">{shop.tagline}</div>}
            <div className="text-[11px] opacity-90 mt-1 flex flex-wrap gap-2">
              {shop.city && <span className="flex items-center gap-1"><MapPin size={11} /> {shop.city}</span>}
              {shop.whatsapp && <a href={`https://wa.me/${shop.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-emerald-200"><MessageCircle size={11} /> {shop.whatsapp}</a>}
            </div>
          </div>
        </div>
      </div>

      {/* Fulfillment strip */}
      <div className="max-w-4xl mx-auto px-3 mt-3">
        {!localStorage.getItem('token') && (
          <a href="/login" data-testid="webshop-public-login-cta"
             className="block mb-2 text-center py-2 rounded-xl bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 text-slate-900 text-xs font-black shadow-lg">
            🔐 Login for Safe Buying · Promo Offers · Local for Vocal Offers 🇮🇳
          </a>
        )}
        <div className="rounded-xl bg-slate-900/70 border border-slate-700 p-2.5 flex flex-wrap items-center gap-2 text-xs" data-testid="public-shop-fulfillment">
          {(shopFulfillment === 'online_delivery' || shopFulfillment === 'both') && (
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-sky-500/20 text-sky-200 font-black">
              <Truck size={12} /> Home Delivery
            </span>
          )}
          {(shopFulfillment === 'offline_pickup' || shopFulfillment === 'both') && (
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-orange-500/20 text-orange-200 font-black">
              <StoreIcon size={12} /> Pickup Available
            </span>
          )}
          {cities.length > 0 && (
            <span className="text-[10px] opacity-80">📍 Delivers to: <b>{cities.join(', ')}</b></span>
          )}
          {shop.pickup_address && (
            <span className="text-[10px] opacity-80">🏪 <b>{shop.pickup_address}</b></span>
          )}
          {installEvent && (
            <button onClick={installPWA} data-testid="public-pwa-install"
                    className="ml-auto px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-[10px] font-black whitespace-nowrap">
              📱 Add to Home
            </button>
          )}
        </div>
      </div>

      {/* Products grid */}
      <div className="max-w-4xl mx-auto px-3 mt-4">
        <div className="text-xs uppercase opacity-70 font-black mb-2 flex items-center gap-1"><ShoppingBag size={12} /> Products ({products.length})</div>
        {products.length === 0 && <div className="text-center opacity-60 py-10">Koi product available nahi.</div>}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {products.map((p) => {
            const fulfil = p.fulfillment || shopFulfillment;
            const yt = ytEmbed(p.video_url);
            return (
              <button
                key={p.id}
                id={`product-${p.id}`}
                onClick={() => { setPick(p); setBuyer((b) => ({ ...b, quantity: Math.max(1, p.moq || 1) })); }}
                data-testid={`public-product-${p.id}`}
                className="rounded-2xl bg-slate-900/80 border-2 border-slate-700 hover:border-emerald-400 overflow-hidden text-left transition"
              >
                <div className="aspect-square bg-slate-800 relative">
                  {p.image_url ? (
                    <img src={abs(p.image_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl">📦</div>
                  )}
                  {p.discount_badge && (
                    <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-rose-500 text-white text-[9px] font-black">{p.discount_badge}</span>
                  )}
                  {(yt || p.video_url) && (
                    <span className="absolute top-1 left-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-black"><Play size={9} /> Video</span>
                  )}
                  <button onClick={(e) => toggleWishlist(e, p)}
                          data-testid={`public-wishlist-${p.id}`}
                          title="Wishlist"
                          className={`absolute bottom-1 right-1 w-8 h-8 rounded-full flex items-center justify-center ${wishlist.has(p.id) ? 'bg-rose-500 text-white' : 'bg-white/80 text-slate-800 hover:bg-white'}`}>
                    <Heart size={14} fill={wishlist.has(p.id) ? 'currentColor' : 'none'} />
                  </button>
                </div>
                <div className="p-2 space-y-1">
                  <div className="text-xs font-black truncate">{p.name}</div>
                  <div className="text-sm font-black text-emerald-300">₹{p.price}{p.moq ? <span className="text-[9px] opacity-70"> · MOQ {p.moq}</span> : null}</div>
                  <div className="flex flex-wrap gap-1">
                    {fulfil === 'online_delivery' && <span className="text-[9px] px-1 py-0.5 rounded bg-sky-500/25 text-sky-200">🚚 Delivery</span>}
                    {fulfil === 'offline_pickup' && <span className="text-[9px] px-1 py-0.5 rounded bg-orange-500/25 text-orange-200">🏪 Pickup</span>}
                    {fulfil === 'both' && <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-500/25 text-emerald-200">🚚+🏪 Both</span>}
                    {p.audio_url && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/25 text-amber-200">🎙</span>}
                    {p.attachment_url && <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-500/25 text-cyan-200">📎</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Product detail modal */}
      {pick && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-2" onClick={() => setPick(null)} data-testid="public-product-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-slate-900 border-2 border-emerald-400/60 max-h-[95vh] overflow-y-auto">
            <div className="relative">
              {pick.image_url && <img src={abs(pick.image_url)} alt="" className="w-full aspect-square object-cover" />}
              <button onClick={() => setPick(null)} className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 text-white font-black" data-testid="public-modal-close">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <h2 className="text-lg font-black">{pick.name}</h2>
                <div className="text-xl font-black text-emerald-300 mt-1">₹{pick.price}{pick.moq ? <span className="text-xs opacity-70"> · MOQ {pick.moq}</span> : null}</div>
              </div>
              {pick.description && <div className="text-xs opacity-90 whitespace-pre-wrap">{pick.description}</div>}

              {/* Video */}
              {pick.video_url && (
                <div className="rounded-xl overflow-hidden border-2 border-slate-700 bg-black" data-testid="public-product-video">
                  {ytEmbed(pick.video_url) ? (
                    <iframe title="video" src={ytEmbed(pick.video_url)} className="w-full aspect-video" allow="autoplay; encrypted-media" allowFullScreen />
                  ) : (
                    <video controls src={abs(pick.video_url)} className="w-full aspect-video bg-black" />
                  )}
                </div>
              )}

              {/* Audio */}
              {pick.audio_url && (
                <audio controls src={abs(pick.audio_url)} className="w-full" data-testid="public-product-audio" />
              )}

              {/* Attachment */}
              {pick.attachment_url && (
                <a href={abs(pick.attachment_url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg bg-cyan-500/15 border border-cyan-400/40 p-2 text-xs font-black text-cyan-200 hover:bg-cyan-500/25" data-testid="public-product-attach">
                  <FileText size={13} /> {pick.attachment_name || 'View attachment'}
                </a>
              )}

              {/* Tier pricing */}
              {pick.tiers?.length > 0 && (
                <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2">
                  <div className="text-[10px] font-black text-emerald-300 uppercase mb-1">💼 Bulk tiered pricing</div>
                  <div className="grid grid-cols-3 gap-1 text-[10px]">
                    {pick.tiers.map((t, i) => (
                      <div key={i} className="rounded bg-slate-900/60 p-1 text-center">
                        <div className="opacity-70">≥ {t.min_qty}</div>
                        <div className="font-black text-emerald-200">₹{t.price}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Order form */}
              <div className="rounded-xl bg-slate-800/40 border border-slate-700 p-3 space-y-2">
                <div className="text-[10px] font-black text-emerald-300 uppercase">📝 Order details</div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} placeholder="Your name" data-testid="public-buyer-name" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
                  <input value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} placeholder="Phone (+91…)" type="tel" data-testid="public-buyer-phone" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
                  <input value={buyer.city} onChange={(e) => setBuyer({ ...buyer, city: e.target.value })} placeholder="Your city" data-testid="public-buyer-city" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
                  <input value={buyer.quantity} onChange={(e) => setBuyer({ ...buyer, quantity: e.target.value })} placeholder="Quantity" type="number" min={pick.moq || 1} data-testid="public-buyer-qty" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
                </div>
                <textarea rows={2} value={buyer.note} onChange={(e) => setBuyer({ ...buyer, note: e.target.value })} placeholder="Message / notes (optional)" data-testid="public-buyer-note" className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs resize-y" />
              </div>

              <button onClick={openWA} disabled={busy} data-testid="public-order-wa"
                      className="w-full py-3 rounded-2xl bg-green-600 hover:bg-green-700 text-white text-sm font-black flex items-center justify-center gap-2 disabled:opacity-50">
                <MessageCircle size={16} /> {busy ? 'Ordering…' : `Order via WhatsApp`}
              </button>
              <div className="text-[10px] text-center opacity-60">Shop owner ko notification bhi mil jaayegi</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
